import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { 
  Users, 
  Trophy, 
  Settings as SettingsIcon, 
  BarChart3, 
  LogOut, 
  User,
  Coins,
  CreditCard,
  Gamepad2,
  UserCircle,
  Menu,
  X,
  Sun,
  Moon,
  Palette,
  Volume2,
  TrendingUp,
  Bell,
  ShoppingBag,
  MessageCircle,
  Shield,
  Crown
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const AdminLayout: React.FC = () => {
  const { logout, currentUser } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navigation = [
    { name: 'Áttekintés', path: '/admin/dashboard', icon: BarChart3 },
    { name: 'Felhasználók', path: '/admin/users', icon: Users },
    { name: 'Játékok', path: '/admin/games', icon: Gamepad2 },
    { name: 'Aranytallérok', path: '/admin/coins', icon: Coins },
    { name: 'Fizetések', path: '/admin/payments', icon: CreditCard },
    { name: 'Termékek', path: '/admin/products', icon: ShoppingBag },
    { name: 'Testreszabás', path: '/admin/customization', icon: Palette },
    { name: 'Hangeffektek', path: '/admin/audio', icon: Volume2 },
    { name: 'Analitika', path: '/admin/analytics', icon: TrendingUp },
    { name: 'Értesítések', path: '/admin/notifications', icon: Bell },
    { name: 'Ügyfélszolgálat', path: '/admin/support', icon: MessageCircle },
    { name: 'Moderáció', path: '/admin/moderation', icon: Shield },
    { name: 'Versenyek', path: '/admin/tournaments', icon: Crown },
    { name: 'Beállítások', path: '/admin/settings', icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity" />
        </div>
      )}

      {/* Mobile sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:hidden ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex h-full flex-col bg-white dark:bg-gray-800 shadow-xl">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-6">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">Game Winners Admin</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 sm:space-y-2 px-3 sm:px-4 py-4">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`sidebar-item ${
                  location.pathname === item.path
                    ? 'sidebar-item-active'
                    : 'sidebar-item-inactive'
                }`}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 shadow-xl">
          <div className="flex items-center justify-between px-6 py-6">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Game Winners Admin</h1>
          </div>
          <nav className="flex-1 space-y-1 sm:space-y-2 px-3 sm:px-4 py-4">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`sidebar-item ${
                  location.pathname === item.path
                    ? 'sidebar-item-active'
                    : 'sidebar-item-inactive'
                }`}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3 sm:py-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-200"
              >
                <Menu className="h-6 w-6" />
              </button>
              <h2 className="ml-3 lg:ml-0 text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                {navigation.find(item => item.path === location.pathname)?.name || 'Admin Panel'}
              </h2>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200"
                title={isDarkMode ? 'Váltás világos módra' : 'Váltás sötét módra'}
              >
                {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>

              {/* Profile Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center space-x-2 sm:space-x-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {currentUser?.displayName || currentUser?.email || 'Admin'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Adminisztrátor
                    </p>
                  </div>
                </button>

                {/* Dropdown Menu */}
                {profileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                    {/* Profile Link */}
                    <Link
                      to="/admin/profile"
                      onClick={() => setProfileDropdownOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <UserCircle className="h-4 w-4 mr-2" />
                      Profil
                    </Link>
                    
                    <hr className="my-1 border-gray-200 dark:border-gray-700" />
                    
                    {/* Logout */}
                    <button
                      onClick={() => {
                        logout();
                        setProfileDropdownOpen(false);
                      }}
                      className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Kijelentkezés
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-4 sm:py-8 animate-fade-in">
          <div className="px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout; 